import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { IMyKnownError, REQUEST_LIFE_CICLE } from 'interfaces/requests';
import { MICROSERVICES, get } from 'api';
import endpoints from './endpoint';
import { IStrategyRegister } from 'components/StrategyRegister/interfaces';
import {
  ISimulation,
  ISimulationResponse,
} from 'components/StrategiesList/SimulationDetailPopUp/DetailTable/interfaces';

import {
  getParsedSimulation,
  getSimulationChangeOnlyHeaders,
  isReadySimulationOrDistribution,
} from './helpers';
import { store } from 'store';
import { loadStrategyAction } from 'modules/strategy';
import { listStrategyAction } from 'modules/strategyList';
import { isLoading } from 'utils/loading';
import {
  CONTAINERS_LIST,
  FORM_MODE,
  MAILING_CONTAINERS_LIST,
} from 'shared/enums';
import { DRILL_DOWN_TIME_INTERVAL } from './configs';

interface ISimulationState {
  loading: REQUEST_LIFE_CICLE;
  list?: ISimulationListByStrategyKey;
}

interface ISimulationListByStrategyKey {
  [strategyIdKey: string]: ISimulation;
}

interface IUpdateSimulationList {
  strategyId: string;
  strategyStatus?: string;
  options?: IUpdateSimulationListOptions;
}

interface IUpdateSimulationListOptions {
  forceLoadCompleteSimulation?: boolean;
}

const initialState = {
  loading: REQUEST_LIFE_CICLE.IDLE,
  list: {},
} as ISimulationState;

const TIME_INTERVAL = DRILL_DOWN_TIME_INTERVAL;
let timeIntervalId = null;
const clearCurrentDrillDownInterval = () => clearInterval(timeIntervalId);

const isLoadingSimulationList = (): boolean => {
  const { loading } = store.getState().simulationList;
  return isLoading([loading]);
};

const continueToSimulating = () => {
  const {
    mode: strategyMode,
  } = store.getState().selectedStrategyModeNavigation;
  const { mode: mailingMode } = store.getState().mailingNavigationMode;
  const mainNavigation = store.getState().mainNavigation;

  if (
    mainNavigation !== CONTAINERS_LIST.MAILING ||
    mailingMode !== MAILING_CONTAINERS_LIST.MAILING_STRATEGIES ||
    (strategyMode !== FORM_MODE.list && strategyMode !== FORM_MODE.listDetail)
  ) {
    clearCurrentDrillDownInterval();
    return false;
  }
  return true;
};

export const drillDownSimulationList = createAsyncThunk<
  void,
  { strategy?: IStrategyRegister },
  { rejectValue: IMyKnownError }
>('simulationList/drillDown', async ({ strategy }, { dispatch }) => {
  clearCurrentDrillDownInterval();

  if (strategy) {
    timeIntervalId = setInterval(function () {
      if (isLoadingSimulationList()) {
        return;
      }

      dispatch(
        updateSimulationListByStrategyAction({
          strategyId: strategy?.id,
          strategyStatus: strategy.status,
        }),
      );
    }, TIME_INTERVAL);
  } else {
    timeIntervalId = setInterval(function () {
      if (isLoadingSimulationList()) {
        return;
      }

      const { list: strategyList } = store.getState().strategyList;

      dispatch(
        updateSimulationListByStrategyListAction({
          strategyList,
        }),
      );
    }, TIME_INTERVAL);
  }
});

export const updateSimulationListByStrategyListAction = createAsyncThunk<
  void,
  { strategyList: IStrategyRegister[]; strategyStatusListToRefresh?: string[] },
  { rejectValue: IMyKnownError }
>(
  'simulationList/updateList',
  async (
    { strategyList, strategyStatusListToRefresh = ['SIMULATING', 'ACTIVE'] },
    { dispatch },
  ) => {
    const strategyListToRefresh = strategyList.filter((strategy) => {
      const stateSimulation = store.getState().simulationList.list?.[
        strategy.id
      ];
      if (
        stateSimulation?.activated ||
        stateSimulation?.status === 'DISTRIBUTION_READY'
      )
        return false;
      if (stateSimulation && strategy.status === 'SIMULATION_READY')
        return false;
      return strategyStatusListToRefresh.includes(strategy.status);
    });

    if (strategyListToRefresh.length) {
      for (let i = 0; i < strategyListToRefresh.length; i++) {
        const strategy = strategyListToRefresh[i];
        if (strategy.id) {
          dispatch(
            updateSimulationListByStrategyAction({
              strategyId: strategy?.id,
              strategyStatus: strategy.status,
            }),
          );
        }
      }
    } else {
      clearCurrentDrillDownInterval();
    }
  },
);

export const updateSimulationListByStrategyAction = createAsyncThunk<
  ISimulation,
  IUpdateSimulationList,
  { rejectValue: IMyKnownError }
>(
  'simulationList/update',
  async ({ strategyId, strategyStatus }, { rejectWithValue, dispatch }) => {
    try {
      if (!continueToSimulating()) {
        return;
      }

      const simulationByStrategyResponse = await dispatch(
        loadSimulationByStrategyAction({
          strategyId,
        }),
      );
      const stateSimulation = store.getState().simulationList.list?.[
        strategyId
      ];
      const simulationByStrategy = ({
        ...simulationByStrategyResponse.payload,
        strategy: strategyId,
      } as unknown) as ISimulationResponse;

      if (stateSimulation?.activated) {
        return {
          ...getParsedSimulation(simulationByStrategy),
          activated: true,
        };
      }

      const oldStatus = stateSimulation?.status;
      const newStatus = simulationByStrategy.status;

      const isReady = isReadySimulationOrDistribution(newStatus);
      if (!isReady) {
        return getSimulationChangeOnlyHeaders(
          stateSimulation,
          simulationByStrategy,
        );
      }

      const params = {} as Partial<ISimulationResponse>;
      if (oldStatus !== newStatus && isReady) {
        const currentStrategy = store.getState().strategy.current;

        if (currentStrategy?.id === strategyId) {
          await dispatch(
            loadStrategyAction({
              id: strategyId,
              mode: FORM_MODE.listDetail,
            }),
          );
          const loadedStrategy = store.getState().strategy.current;

          if (['SIMULATING'].includes(loadedStrategy.status)) {
            params.status = oldStatus;
          }
        }
        // else {
        //   dispatch(listStrategyAction({}));
        // }
      }
      return {
        ...getParsedSimulation(simulationByStrategy),
        ...params,
        activated: strategyStatus === 'ACTIVE',
      };
    } catch (error) {
      console.error(
        `error at update simulation list with strategy ${strategyId}`,
        error,
      );
      return rejectWithValue(error);
    }
  },
);

export const loadSimulationByStrategyAction = createAsyncThunk<
  ISimulationResponse,
  IUpdateSimulationList,
  { rejectValue: IStrategyRegister }
>('simulationList/get', async ({ strategyId }, { rejectWithValue }) => {
  try {
    const response = await get<ISimulationResponse[]>(
      MICROSERVICES.DISTRIBUTIONS,
      {
        url: endpoints.loadSimulation(strategyId),
      },
    );
    const simulationDistribution = response.data.values[0];
    return simulationDistribution;
  } catch (error) {
    console.error(`error at show simulation ${strategyId}`, error);
    return rejectWithValue(error);
  }
});

const simulationListModeSlice = createSlice({
  name: 'simulationList',
  initialState,
  reducers: {
    resetSimulationListAction(state) {
      clearCurrentDrillDownInterval();
      state = Object.assign({}, initialState);
      return state;
    },
    clearDrillDownInterval(state) {
      clearCurrentDrillDownInterval();
      return state;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateSimulationListByStrategyAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
      return state;
    });
    builder.addCase(updateSimulationListByStrategyAction.rejected, (state) => {
      state.loading = REQUEST_LIFE_CICLE.FAILED;
      return state;
    });
    builder.addCase(
      updateSimulationListByStrategyAction.fulfilled,
      (state, { payload }) => {
        state.loading = REQUEST_LIFE_CICLE.SUCCEEDED;
        state.list = payload
          ? { ...state.list, [payload.strategy]: payload }
          : state.list;

        return state;
      },
    );
  },
});
const { reducer, actions } = simulationListModeSlice;
export const { resetSimulationListAction, clearDrillDownInterval } = actions;
export default reducer;
